import { ArrowLeftOutlined, ArrowRightOutlined, DeleteOutlined, ExperimentOutlined, UploadOutlined } from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { App, Button, Card, Col, Empty, Flex, Form, Input, InputNumber, Result, Row, Select, Skeleton, Space, Typography, Upload } from "antd";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { ApiError, api, unwrap } from "@/api/client";
import { useApplyFieldErrors, useErrorText } from "@/api/errors";
import { useAuth } from "@/auth/AuthProvider";
import { useTabTitle } from "@/components/tabs/WorkTabs";
import { useI18n, type MessageKey } from "@/i18n";
import { PlatformLabel } from "../channels/PlatformBadge";
import { ChannelStatusTag, ProductImage, usePublishStores, type Product } from "./Products";
import { AiCopyModal, type AiRequest } from "./AiCopy";
import { StoreDrawer } from "./Publish";
import { combos, keyOf, matrixOf, matrixProblem, VariantMatrix, type Matrix, type SkuLabel } from "./VariantMatrix";

type Values = {
  name: string;
  description?: string;
  brand?: string;
  weightG?: number | null;
  lengthCm?: number | null;
  widthCm?: number | null;
  heightCm?: number | null;
  price?: number | null;
  status?: "active" | "archived";
};

/** Produk baru / detail produk: the master product, its pictures, and where it is published. */
export function ProductEditor() {
  const { productID = "new" } = useParams();
  const isNew = productID === "new";
  const { t } = useI18n();
  const { can } = useAuth();
  const { message, modal } = App.useApp();
  const errorText = useErrorText();
  const applyFieldErrors = useApplyFieldErrors();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const manage = can("catalog.manage");
  const [form] = Form.useForm<Values>();
  // Variants (variations and their combinations) live beside the form.
  const [matrix, setMatrix] = useState<Matrix>({ groups: [], rows: {} });
  const [matrixTouched, setMatrixTouched] = useState(false);
  const [labels, setLabels] = useState<Record<string, SkuLabel>>({});
  const changeMatrix = (m: Matrix) => (setMatrix(m), setMatrixTouched(true));
  const [store, setStore] = useState<{ id: string; name: string; platform: Product["channels"][number]["platform"] } | null>(null);
  const [ai, setAi] = useState<AiRequest | null>(null);
  const q = useQuery({
    queryKey: ["products", "detail", productID],
    queryFn: () => unwrap(api.GET("/v1/products/{productID}", { params: { path: { productID } } })),
    enabled: !isNew,
    retry: (n, err) => !(err instanceof ApiError && err.status === 404) && n < 2,
  });
  const p = q.data;
  useTabTitle(isNew ? t("prod.add") : p?.name);
  useEffect(() => {
    if (!p) return;
    form.setFieldsValue({
      name: p.name,
      description: p.description,
      brand: p.brand,
      weightG: p.weightG,
      lengthCm: p.lengthCm,
      widthCm: p.widthCm,
      heightCm: p.heightCm,
      price: p.price,
      status: p.status,
    });
  }, [p, form]);
  // The variants follow the product as loaded (or saved).
  const [loaded, setLoaded] = useState<Product>();
  if (p && p !== loaded) {
    setLoaded(p);
    setMatrix(matrixOf(p));
    setMatrixTouched(false);
    setLabels((l) => ({ ...l, ...Object.fromEntries(p.variants.map((v) => [v.skuId, { code: v.skuCode, name: v.skuName }])) }));
  }
  const productName = Form.useWatch("name", form) ?? "";

  const save = useMutation({
    mutationFn: (v: Values) => {
      const groups = matrix.groups.map((g) => ({ name: g.name.trim(), values: g.values }));
      const body = {
        name: v.name.trim(),
        description: v.description ?? "",
        brand: v.brand ?? "",
        weightG: v.weightG ?? null,
        lengthCm: v.lengthCm ?? null,
        widthCm: v.widthCm ?? null,
        heightCm: v.heightCm ?? null,
        price: v.price ?? null,
        optionNames: groups.map((g) => g.name),
        variants: combos(groups).map((c) => {
          const r = matrix.rows[keyOf(c)] ?? {};
          return { skuId: r.skuId!, options: c, price: r.price ?? null, imageId: r.imageId ?? null };
        }),
      };
      return isNew
        ? unwrap(api.POST("/v1/products", { body }))
        : unwrap(api.PUT("/v1/products/{productID}", { params: { path: { productID } }, body: { ...body, status: v.status ?? "active" } }));
    },
    onSuccess: (x) => {
      queryClient.setQueryData(["products", "detail", x.id], x);
      void queryClient.invalidateQueries({ queryKey: ["products", "list"] });
      void message.success(t("prod.saved"));
      if (isNew) navigate(`/app/products/${x.id}`, { replace: true });
    },
    onError: (err) => {
      if (!applyFieldErrors(form, err)) void message.error(errorText(err));
    },
  });

  // Batal: back to the list; unsaved changes are asked about first.
  const cancel = () => {
    const leave = () => navigate("/app/products");
    if (!manage || (!form.isFieldsTouched() && !matrixTouched)) return leave();
    modal.confirm({
      title: t("prod.discardTitle"),
      content: t("prod.discardText"),
      okText: t("prod.discard"),
      okButtonProps: { danger: true },
      cancelText: t("prod.keepEditing"),
      onOk: leave,
    });
  };

  if (q.isError) {
    return q.error instanceof ApiError && q.error.status === 404 ? <Result status="404" title={t("page.notFound.title")} /> : <Result status="error" title={errorText(q.error)} />;
  }
  if (!isNew && !p) return <Skeleton active />;
  const gallery = (p?.images ?? []).filter((i) => i.role === "gallery");
  const submit = () => {
    const problem = matrixProblem(matrix);
    if (problem) return void message.error(t(problem as MessageKey));
    form.submit();
  };

  return (
    <>
      <Flex justify="space-between" align="center" wrap gap={12} style={{ marginBottom: 16 }}>
        <Typography.Title level={2} style={{ margin: 0, fontSize: 28 }}>
          {isNew ? t("prod.add") : p!.name}
        </Typography.Title>
        <Space>
          <Button onClick={cancel}>{t(manage ? "common.cancel" : "common.back")}</Button>
          {manage && (
            <Button type="primary" loading={save.isPending} onClick={submit}>
              {t("common.save")}
            </Button>
          )}
        </Space>
      </Flex>
      <Form
        form={form}
        layout="vertical"
        requiredMark={false}
        disabled={!manage}
        initialValues={{ status: "active" }}
        onFinish={(v) => save.mutate(v)}
      >
        <Row gutter={[16, 16]}>
          <Col xs={24} xl={16}>
            <Space orientation="vertical" size={16} style={{ width: "100%" }}>
              <Card
                size="small"
                title={t("prod.info")}
                extra={
                  !isNew &&
                  manage && (
                    <Button size="small" icon={<ExperimentOutlined />} onClick={() => setAi({})}>
                      {t("prod.ai.write")}
                    </Button>
                  )
                }
              >
                <Form.Item name="name" label={t("prod.name")} rules={[{ required: true, whitespace: true, max: 255 }]} extra={t("prod.nameHelp")}>
                  <Input showCount maxLength={255} />
                </Form.Item>
                <Form.Item name="description" label={t("prod.description")} extra={t("prod.descriptionHelp")}>
                  <Input.TextArea rows={6} maxLength={10000} showCount />
                </Form.Item>
                <Row gutter={16}>
                  <Col xs={24} md={12}>
                    <Form.Item name="brand" label={t("prod.brand")}>
                      <Input maxLength={100} />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item name="price" label={t("prod.price")} extra={t("prod.priceHelp")}>
                      <InputNumber min={1} precision={0} prefix="Rp" style={{ width: "100%" }} />
                    </Form.Item>
                  </Col>
                </Row>
                {!isNew && (
                  <Form.Item name="status" label={t("common.status")}>
                    <Select
                      style={{ width: 200 }}
                      options={[
                        { value: "active", label: t("prod.show.active") },
                        { value: "archived", label: t("prod.show.archived") },
                      ]}
                    />
                  </Form.Item>
                )}
              </Card>

              <VariantMatrix
                value={matrix}
                onChange={changeMatrix}
                labels={labels}
                onLabel={(id, l) => setLabels((x) => ({ ...x, [id]: l }))}
                productName={productName}
                gallery={gallery}
                disabled={!manage}
              />

              <Card size="small" title={t("prod.shipping")}>
                <Row gutter={16}>
                  <Col xs={12} md={6}>
                    <Form.Item name="weightG" label={t("prod.weight")}>
                      <InputNumber min={1} precision={0} suffix="g" style={{ width: "100%" }} />
                    </Form.Item>
                  </Col>
                  {(["lengthCm", "widthCm", "heightCm"] as const).map((k) => (
                    <Col xs={12} md={6} key={k}>
                      <Form.Item name={k} label={t(`prod.${k}`)}>
                        <InputNumber min={1} precision={0} suffix="cm" style={{ width: "100%" }} />
                      </Form.Item>
                    </Col>
                  ))}
                </Row>
              </Card>
            </Space>
          </Col>
          <Col xs={24} xl={8}>
            <Space orientation="vertical" size={16} style={{ width: "100%" }}>
              {p ? <Gallery product={p} manage={manage} /> : <Card size="small" title={t("prod.photos")}><Typography.Text type="secondary">{t("prod.saveFirst")}</Typography.Text></Card>}
              {p ? (
                <StoresCard product={p} manage={manage} onOpen={setStore} />
              ) : (
                <Card size="small" title={t("prod.stores")}>
                  <Typography.Text type="secondary">{t("prod.saveFirst")}</Typography.Text>
                </Card>
              )}
            </Space>
          </Col>
        </Row>
      </Form>
      {p && (
        <StoreDrawer
          product={p}
          store={store}
          onClose={() => setStore(null)}
          onFix={manage ? (connectionId) => (setStore(null), setAi({ connectionId, fix: true })) : undefined}
        />
      )}
      {p && ai && (
        <AiCopyModal
          product={p}
          request={ai}
          onClose={() => setAi(null)}
          onApply={(name, description) => {
            form.setFieldsValue({ name, description });
            setAi(null);
            void message.success(t("prod.ai.used"), 6);
          }}
        />
      )}
    </>
  );
}

/** Foto: up to nine; the first is the cover. */
function Gallery({ product, manage }: { product: Product; manage: boolean }) {
  const { t } = useI18n();
  const { message } = App.useApp();
  const errorText = useErrorText();
  const queryClient = useQueryClient();
  const gallery = product.images.filter((i) => i.role === "gallery");
  const set = (x: Product) => queryClient.setQueryData(["products", "detail", product.id], x);
  const refresh = () => void queryClient.invalidateQueries({ queryKey: ["products", "detail", product.id] });
  const upload = useMutation({
    mutationFn: (f: File) =>
      unwrap(
        api.POST("/v1/products/{productID}/images", {
          params: { path: { productID: product.id }, query: { role: "gallery" } },
          body: f as unknown as string,
          bodySerializer: (b: unknown) => b as Blob,
          headers: { "Content-Type": f.type },
        }),
      ),
    onSuccess: refresh,
    onError: (err) => void message.error(errorText(err)),
  });
  const remove = useMutation({
    mutationFn: (id: string) => unwrap(api.DELETE("/v1/products/{productID}/images/{imageID}", { params: { path: { productID: product.id, imageID: id } } })),
    onSuccess: refresh,
    onError: (err) => void message.error(errorText(err)),
  });
  const order = useMutation({
    mutationFn: (ids: string[]) => unwrap(api.PUT("/v1/products/{productID}/images/order", { params: { path: { productID: product.id } }, body: { ids } })),
    onSuccess: set,
    onError: (err) => void message.error(errorText(err)),
  });
  const move = (i: number, by: number) => {
    const ids = gallery.map((g) => g.id);
    const [x] = ids.splice(i, 1);
    ids.splice(i + by, 0, x!);
    order.mutate(ids);
  };
  return (
    <Card
      size="small"
      title={t("prod.photos")}
      extra={
        manage &&
        gallery.length < 9 && (
          <Upload
            accept="image/jpeg,image/png"
            multiple
            showUploadList={false}
            beforeUpload={(f) => {
              upload.mutate(f);
              return false;
            }}
          >
            <Button size="small" icon={<UploadOutlined />} loading={upload.isPending}>
              {t("prod.uploadPhoto")}
            </Button>
          </Upload>
        )
      }
    >
      {gallery.length === 0 ? (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t("prod.noPhotos")} />
      ) : (
        <Flex wrap gap={12}>
          {gallery.map((im, i) => (
            <Space key={im.id} orientation="vertical" size={4} align="center">
              <ProductImage productId={product.id} imageId={im.id} size={88} />
              <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                {i === 0 ? t("prod.cover") : `${im.width}×${im.height}`}
              </Typography.Text>
              {manage && (
                <Space size={0}>
                  <Button size="small" type="text" icon={<ArrowLeftOutlined />} disabled={i === 0} onClick={() => move(i, -1)} />
                  <Button size="small" type="text" danger icon={<DeleteOutlined />} onClick={() => remove.mutate(im.id)} />
                  <Button size="small" type="text" icon={<ArrowRightOutlined />} disabled={i === gallery.length - 1} onClick={() => move(i, 1)} />
                </Space>
              )}
            </Space>
          ))}
        </Flex>
      )}
      <Typography.Paragraph type="secondary" style={{ fontSize: 12, marginTop: 12, marginBottom: 0 }}>
        {t("prod.photosHelp")}
      </Typography.Paragraph>
    </Card>
  );
}

/** Toko: where the product is published, and "Atur & publikasikan" per store. */
function StoresCard({ product, manage, onOpen }: { product: Product; manage: boolean; onOpen: (s: { id: string; name: string; platform: Product["channels"][number]["platform"] }) => void }) {
  const { t } = useI18n();
  const stores = usePublishStores();
  const rows = [
    ...product.channels.map((c) => ({ id: c.connectionId, name: c.shopName, platform: c.platform, channel: c })),
    ...stores.filter((s) => !product.channels.some((c) => c.connectionId === s.id)).map((s) => ({ id: s.id, name: s.name, platform: s.platform, channel: undefined })),
  ];
  return (
    <Card size="small" title={t("prod.stores")}>
      {rows.length === 0 ? (
        <Typography.Text type="secondary">{t("prod.noStores")}</Typography.Text>
      ) : (
        <Space orientation="vertical" size={12} style={{ width: "100%" }}>
          {rows.map((r) => (
            <Flex key={r.id} justify="space-between" align="center" gap={8} wrap>
              <Space orientation="vertical" size={2}>
                <Space size={6}>
                  <PlatformLabel platform={r.platform} />
                  <Typography.Text>{r.name}</Typography.Text>
                </Space>
                {r.channel ? <ChannelStatusTag status={r.channel.status} /> : <Typography.Text type="secondary">{t("prod.status.not_set")}</Typography.Text>}
              </Space>
              {manage && (
                <Button size="small" onClick={() => onOpen({ id: r.id, name: r.name, platform: r.platform })}>
                  {r.channel?.externalId ? t("prod.store.manage") : t("prod.store.setup")}
                </Button>
              )}
            </Flex>
          ))}
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {t("prod.storesHelp")}
          </Typography.Text>
        </Space>
      )}
    </Card>
  );
}
